import { v4 as uuidv4 } from 'uuid';
import { dragDropScrolling } from '@nsr/react-drag-drop-control';
import { CONSTANTS } from '../../Helpers/Configuration';
import { colorCtaBlueBase } from '@ellucian/react-design-system/core/styles/tokens';
import { isEthosPathArray, reorderAllControls } from '../../Helpers/TemplateHelpers';
import { resetHoverStyles } from './dragDropHelper';

const { addEventListenerForContainer, removeEventListenerForContainer } = dragDropScrolling;
const maxCols = 3;
const { controlTypes, appTemplateControlTypes } = CONSTANTS;
const hoverStates = {
    sameControl: 'sameControl',
    empty: 'empty',
    sameRowHorizontalLeft: 'sameRowHorizontalLeft',
    differentRowVerticalTop: 'differentRowVerticalTop',
    differentRowHorizontalLeft: 'differentRowHorizontalLeft',
    none: 'none',
};
const hoverLocations = { none: 'none', left: 'left', right: 'right', top: 'top', bottom: 'bottom' };
const transitionStyle = { transition: 'padding 0.1s 0s, border 0.1s 0s, width 0.1s 0s' };
const paddingStyleLeft = { padding: '0 0 0 84px', width: 'calc(100% + 84px)' };
const paddingStyleTop = { padding: '48px 0 0 0' };
const emptyStyle = { border: `1px dashed ${colorCtaBlueBase}` };
let prevHoverState = hoverStates.sameControl;
let prevDragId = 0;
let prevHoverId = 0;

const getOnDragQuestionData = (groups, questions, dragId, hoverId) => {
    let dragQuestion, dragQuestionIndex, dragGroup, dragGroupIndex, hoverQuestion, hoverQuestionIndex, hoverGroup, hoverGroupIndex;
    dragQuestion = questions.find(q => q.id === dragId);
    dragQuestionIndex = questions.findIndex(q => q.id === dragId);
    dragGroup = groups.find(g => g.id === dragQuestion.groupId);
    dragGroupIndex = groups.findIndex(g => g.id === dragQuestion.groupId);

    hoverQuestion = questions.find(q => q.id === hoverId);
    hoverQuestionIndex = questions.findIndex(q => q.id === hoverId);
    hoverGroup = groups.find(g => g.id === hoverQuestion.groupId);
    hoverGroupIndex = groups.findIndex(g => g.id === hoverQuestion.groupId);

    return { dragQuestion, dragQuestionIndex, dragGroup, dragGroupIndex, hoverQuestion, hoverQuestionIndex, hoverGroup, hoverGroupIndex };
};

const addEmptyQuestions = (allSections, allGroups, allQuestions) => {
    const emptyQuestion = { labelId: '', type: controlTypes.empty, errors: { requiredError: false, conditionalError: false } };
    const { columnWidthOptions } = CONSTANTS;
    let newGroups = [], newQuestions = [];
    allSections.forEach(s => {
        const groupsInSection = allGroups.filter(g => g.sectionId === s.id);
        groupsInSection.forEach(g => {
            // If not empty, push to new array
            if (allQuestions.some(q => q.groupId === g.id && q.type !== controlTypes.empty)) {
                newGroups.push(g);

                const questionsInGroup = allQuestions.filter(q => q.groupId === g.id);
                const questionsLen = questionsInGroup.length;
                const isExtended = questionsLen == 1 && questionsInGroup[0]?.columnWidth == columnWidthOptions.extended;
                questionsInGroup.forEach((q, i) => {
                    if (i < maxCols) {
                        newQuestions.push(q);
                    }
                });
                // Insert empty at the end
                if (!isExtended && questionsLen < maxCols && questionsInGroup[questionsLen - 1].type !== controlTypes.empty) {
                    newQuestions.push({
                        ...emptyQuestion, id: uuidv4(), groupId: g.id, sectionId: s.id, pageId: s.pageId,
                    });
                }
            }
        });
        // Add group with empty question
        const newGroup = { id: uuidv4(), sectionId: s.id, pageId: s.pageId };
        newGroups.push(newGroup);
        newQuestions.push({ ...emptyQuestion, id: uuidv4(), groupId: newGroup.id, sectionId: s.id, pageId: s.pageId });
    });
    return { newGroups, newQuestions };
};

export const beginMoveTypeQuestion = (questions) => {
    return resetHoverStyles(questions);
};

export const moveTypeQuestion = (dragId, hoverId, dragRef, dragMonitor, domContainerId, controlGrid, hoverStyles) => {
    const [, , groups, questions] = controlGrid;
    let newHoverStyles = { ...hoverStyles };
    const { dragQuestionIndex, dragGroupIndex, dragGroup,
        hoverQuestion, hoverQuestionIndex, hoverGroup, hoverGroupIndex
    } = getOnDragQuestionData(groups, questions, dragId, hoverId);

    addEventListenerForContainer(domContainerId);
    let loc = hoverLocations.none;
    if (hoverQuestion.type !== controlTypes.empty) {
        // Determine mouse position
        const clientOffset = dragMonitor.getClientOffset();
        if (!clientOffset) { return newHoverStyles; }

        // Determine rectangle on screen
        const hoverBoundingRect = dragRef.current.getBoundingClientRect();
        // Get vertical middle
        const hoverMiddleY =
            (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        // Get horizontal 1/4 and 3/4
        const hoverLeftX =
            (hoverBoundingRect.right - hoverBoundingRect.left) / 4;
        const hoverRightX =
            (hoverBoundingRect.right - hoverBoundingRect.left) * 0.75;
        // Get pixels to the top
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;
        // Get pixels to the left
        const hoverClientX = clientOffset.x - hoverBoundingRect.left;

        if (hoverClientX < hoverLeftX) { loc = hoverLocations.left; }
        else if (hoverClientX > hoverRightX) { loc = hoverLocations.right; }
        else if (hoverClientY < hoverMiddleY) { loc = hoverLocations.top; }
        else if (hoverClientY > hoverMiddleY) { loc = hoverLocations.bottom; }
        else { loc = hoverLocations.none; }
    }

    let hoverState;
    const questionsInHoverGroup = questions.filter(q => q.groupId === hoverGroup.id);
    const questionsInDragGroup = questions.filter(q => q.groupId === dragGroup.id);
    // Hover on same question
    if (dragId === hoverId) {
        hoverState = hoverStates.sameControl;
    }
    // Hover on an empty question
    else if (hoverQuestion.type === controlTypes.empty && (dragGroupIndex !== hoverGroupIndex ||
        hoverQuestionIndex - dragQuestionIndex !== 1)) {
        hoverState = hoverStates.empty;
    }
    // Hover on the left part of an question in the same row
    else if (dragGroupIndex === hoverGroupIndex &&
        loc === hoverLocations.left && hoverQuestionIndex - dragQuestionIndex !== 1) {
        hoverState = hoverStates.sameRowHorizontalLeft;
    }
    // Hover on the top part of an question in the another row
    else if (loc === hoverLocations.top &&
        (hoverGroupIndex - dragGroupIndex !== 1 || questionsInDragGroup.filter(q => q.type !== controlTypes.empty).length > 1)) {
        hoverState = hoverStates.differentRowVerticalTop;
    }
    // Hover on the left part of an question in the another row
    else if (dragGroupIndex !== hoverGroupIndex && loc === hoverLocations.left &&
        questionsInHoverGroup.filter(q => q.type !== controlTypes.empty).length < maxCols) {
        hoverState = hoverStates.differentRowHorizontalLeft;
    }
    else { hoverState = hoverStates.none; }

    // Same as previous relevant hover area
    if (hoverState === prevHoverState && hoverId === prevHoverId && dragId === prevDragId) {
        return newHoverStyles;
    }
    prevHoverState = hoverState;
    prevHoverId = hoverId;
    prevDragId = dragId;
    newHoverStyles = resetHoverStyles(questions);
    newHoverStyles[dragId] = { opacity: 0.5 };

    // Set the hoverStyles based on the hover state
    switch (hoverState) {
        case hoverStates.sameControl:
            break;
        case hoverStates.empty:
            newHoverStyles[hoverId] = { ...transitionStyle, ...emptyStyle };
            break;
        case hoverStates.sameRowHorizontalLeft: {
            // Apply styles to all questions in group starting with the hovered question    
            let foundHovered = false;
            questionsInHoverGroup.forEach(q => {
                if (q.id === hoverId) { foundHovered = true; }
                if (foundHovered) {
                    newHoverStyles[q.id] = { ...transitionStyle, ...paddingStyleLeft };
                }
            });
            break;
        }
        case hoverStates.differentRowVerticalTop:
            questionsInHoverGroup.forEach(q => {
                newHoverStyles[q.id] = { ...transitionStyle, ...paddingStyleTop };
            });
            break;
        case hoverStates.differentRowHorizontalLeft: {
            if (questionsInHoverGroup.length === maxCols) {
                newHoverStyles[questionsInHoverGroup.find(q => q.type === controlTypes.empty).id] = { display: 'none' };
            }
            // Apply styles to all questions in group starting with the hovered question    
            let foundHovered = false;
            questionsInHoverGroup.forEach(q => {
                if (q.id === hoverId) { foundHovered = true; }
                if (foundHovered) {
                    newHoverStyles[q.id] = { ...transitionStyle, ...paddingStyleLeft };
                }
            });
            break;
        }
        default:
    }
    return newHoverStyles;
};

export const dropTypeQuestion = (dragId, hoverId, controlGrid, selectedControl) => {
    const [pages, sections, groups, questions] = controlGrid;
    let newGroups = [...groups];
    let newQuestions = [...questions];
    const { dragQuestion, dragQuestionIndex, dragGroupIndex,
        hoverQuestion, hoverQuestionIndex, hoverGroupIndex
    } = getOnDragQuestionData(groups, questions, dragId, hoverId);

    const returnState = {
        dndHoverStyles: resetHoverStyles(newQuestions),
        page: { newSections: sections, newGroups, newQuestions },
        selectedControl,
        dragDropErrors: []
    };
    removeEventListenerForContainer();
    const errors = validateDrop(dragQuestion, pages, sections, questions, hoverQuestion, prevHoverState);
    
    if (errors.length) {
        returnState.dragDropErrors = errors;
        return returnState;
    }
    switch (prevHoverState) {
        // Replace empty with dragged question
        case hoverStates.empty:
            newQuestions[hoverQuestionIndex] = { ...dragQuestion, groupId: hoverQuestion.groupId, sectionId: hoverQuestion.sectionId };
            newQuestions.splice(dragQuestionIndex, 1);
            break;
        // Remove from drag and insert into left of hover position
        case hoverStates.sameRowHorizontalLeft:
            if (hoverQuestionIndex < dragQuestionIndex) {
                newQuestions.splice(hoverQuestionIndex, 0, newQuestions.splice(dragQuestionIndex, 1)[0]);
            }
            else {
                newQuestions.splice(hoverQuestionIndex, 0, dragQuestion);
                newQuestions.splice(dragQuestionIndex, 1);
            }
            break;
        // Create new group above hover position and replace the dragged question
        case hoverStates.differentRowVerticalTop: {
            const insertOffset = hoverGroupIndex > dragGroupIndex ? -1 : 0;
            const newGroup = { id: uuidv4(), sectionId: hoverQuestion.sectionId, pageId: hoverQuestion.pageId };
            newGroups.splice(hoverGroupIndex, 0, newGroup);
            newQuestions.splice(hoverQuestionIndex + insertOffset, 0, {
                ...newQuestions.splice(dragQuestionIndex, 1)[0],
                groupId: newGroup.id,
                sectionId: hoverQuestion.sectionId,
            });
            break;
        }
        // Remove from drag and insert into left of hover position
        case hoverStates.differentRowHorizontalLeft: {
            const insertOffset = hoverQuestionIndex > dragQuestionIndex ? -1 : 0;
            newQuestions.splice(hoverQuestionIndex + insertOffset, 0, {
                ...newQuestions.splice(dragQuestionIndex, 1)[0],
                groupId: hoverQuestion.groupId,
                sectionId: hoverQuestion.sectionId,
            });
            break;
        }
        default:
            return returnState;
    }
    const result = addEmptyQuestions(sections, newGroups, newQuestions);
    newGroups = result.newGroups;
    newQuestions = result.newQuestions;

    returnState.dndHoverStyles = resetHoverStyles(newQuestions);
    returnState.page = reorderAllControls(pages, sections, newGroups, newQuestions);
    returnState.selectedControl = {
        controlType: appTemplateControlTypes.question,
        pageId: dragQuestion.pageId,
        sectionId: newQuestions.find(q => q.id === dragId).sectionId,
        groupId: newQuestions.find(q => q.id === dragId).groupId,
        questionId: dragId,
    };
    return returnState;
};

export const undropTypeQuestion = (questions) => {
    removeEventListenerForContainer();
    return resetHoverStyles(questions);
};

const validateDrop = (dragQuestion, pages, sections, questions, hoverQuestion, prevHoverState) => {
    const errors = [];
    const { columnWidthOptions, controlTypes } = CONSTANTS;
    const isQuestionSystemRequired = dragQuestion?.systemRequired ?? false;
    const isPageVisibleConditionally = pages?.find((page) => page.id === hoverQuestion.pageId)?.conditions?.length > 0;
    const section = sections?.find((section) => section.id === hoverQuestion.sectionId);
    const isSectionVisibleConditionally = section?.conditions?.length > 0;
    const isSectionRepeating = section?.showRepetition || false;

    if (isQuestionSystemRequired && (isPageVisibleConditionally || isSectionVisibleConditionally)) {
        errors.push('errorMoveingASystemRequiredField');
    }

    if (
        dragQuestion.columnWidth == columnWidthOptions.extended &&
        (prevHoverState === hoverStates.empty || prevHoverState === hoverStates.differentRowHorizontalLeft)
    ) {
        const questionsInDropGroup = questions.filter(q => q.groupId == hoverQuestion.groupId);

        // If there are multiple questions, or if there is one non-empty question
        if (questionsInDropGroup.length > 1 || (questionsInDropGroup.length == 1 && questionsInDropGroup[0]?.type != controlTypes.empty)) {
            errors.push('appDesignColumnWidthDropError');
        }
    }

    if (isSectionRepeating && !isEthosPathArray(dragQuestion?.eedmDestinationAttribute)) {
        errors.push('appDesignDragRepetitionError');
    }

    return errors;
};
